import styled from 'styled-components';
import { motion } from 'framer-motion';

export const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, var(--background-secondary, var(--bg-secondary)) 0%, var(--background-primary, var(--bg-primary)) 100%);
  padding: 5rem 1rem 2rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 1rem 0.75rem;
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.55rem;
  }
`;

export const CheckInCard = styled(motion.div)`
  background: var(--background-primary, var(--bg-primary));
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 2rem;
  max-width: 620px;
  width: 100%;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
  margin-top: 0;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    border-radius: 14px;
    max-width: 100%;
    margin-top: 0;
  }

  @media (max-width: 480px) {
    padding: 0.9rem;
    border-radius: 12px;
  }
`;

export const Header = styled.div`
  text-align: center;
  margin-bottom: 1.35rem;

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

export const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.4rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;

  svg {
    width: 1.15rem;
    height: 1.15rem;
    color: var(--success-dark, var(--success-color));
  }
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.35rem;
  }
`;

export const SessionInfo = styled.div`
  background: var(--background-secondary, var(--bg-secondary));
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 1.2rem;
  margin-bottom: 1.25rem;
  
  @media (max-width: 768px) {
    padding: 0.95rem;
    border-radius: 12px;
  }
`;

export const SessionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.8rem 0;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }

  @media (max-width: 480px) {
    font-size: 1.05rem;
  }
`;

export const SessionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 480px) {
    gap: 0.6rem;
  }
`;

export const SessionDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.95rem;
  color: var(--text-secondary);

  svg {
    color: var(--text-muted);
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    font-size: 0.88rem;
    line-height: 1.35;
    align-items: flex-start;
  }
`;

export const CheckInSection = styled.div`
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
  }
`;

export const CheckInButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

export const CheckInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.9rem 1.2rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--background-primary, var(--bg-primary));
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 52px;
  
  &:hover {
    background: var(--background-secondary, var(--bg-secondary));
    border-color: var(--primary-color);
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  }
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover {
      background: var(--primary-hover);
    }
  }
  
  &.success {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border-color: var(--success-color);
    
    &:hover {
      background: rgba(104, 211, 145, 0.2);
    }
  }

  &.successSolid {
    background: #d9f7e6;
    color: #166534;
    border-color: #86efac;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    line-height: 1.2;

    &:hover {
      background: #c8f0da;
      border-color: #68d391;
    }
  }
  
  &.error {
    background: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
    border-color: var(--error-color);
    
    &:hover {
      background: rgba(252, 129, 129, 0.2);
    }
  }
  
  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    min-height: 48px;
    padding: 0.78rem 0.9rem;
    gap: 0.5rem;
  }
`;

export const StatusMessage = styled(motion.div)`
  padding: 0.9rem 1rem;
  border-radius: 12px;
  font-size: 0.92rem;
  text-align: left;
  margin-bottom: 0.85rem;
  line-height: 1.45;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  svg {
    margin-top: 0.1rem;
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    font-size: 0.86rem;
    padding: 0.78rem 0.82rem;
  }
  
  &.success {
    background: rgba(104, 211, 145, 0.1);
    border: 1px solid var(--success-color);
    color: var(--success-color);
  }
  
  &.error {
    background: rgba(252, 129, 129, 0.1);
    border: 1px solid var(--error-color);
    color: var(--error-color);
  }
  
  &.info {
    background: rgba(96, 165, 250, 0.1);
    border: 1px solid var(--info-color);
    color: var(--info-color);
  }
`;

export const StatusMessageText = styled.span`
  flex: 1;
  min-width: 0;
`;

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 1.5rem 1rem;
  color: var(--text-secondary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
`;

export const EmptyIcon = styled.div`
  width: 76px;
  height: 76px;
  margin-bottom: 1rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: linear-gradient(180deg, rgba(245, 173, 62, 0.18) 0%, rgba(245, 173, 62, 0.08) 100%);
  color: #B7791F;

  svg {
    width: 36px;
    height: 36px;
  }
`;

export const EmptyTitle = styled.h3`
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;

  @media (max-width: 768px) {
    font-size: 1.55rem;
  }
`;

export const EmptyDescription = styled.p`
  color: var(--text-secondary);
  font-size: 1.12rem;
  line-height: 1.55;
  margin: 0;
  max-width: 520px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const TimeWindowInfo = styled.div`
  background: rgba(96, 165, 250, 0.08);
  border: 1px solid rgba(96, 165, 250, 0.3);
  border-radius: 12px;
  padding: 1rem;
  margin: 1rem 0;
`;

export const TimeWindowDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;

  @media (max-width: 480px) {
    align-items: flex-start;
    line-height: 1.35;

    svg {
      margin-top: 0.15rem;
      flex-shrink: 0;
    }
  }
`;

export const TimeWindowRestricted = styled.div`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  border-radius: 12px;
  padding: 1rem;
  margin: 1rem 0;
  text-align: left;
`;

export const TimeWindowRestrictedMessage = styled.p`
  font-size: 0.92rem;
  color: var(--error-color);
  margin: 0;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  svg {
    margin-top: 0.08rem;
    flex-shrink: 0;
  }
`;

export const TimeWindowRestrictedMessageText = styled.span`
  flex: 1;
  min-width: 0;
`;
